import { useEstabsContext } from '../../contexts/EstabsContext';
import { useState } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useAppContext } from '../../contexts/AppContext';
import { useParamsContext } from '../../contexts/ParamsContext';
import { formatBRL } from '../agenda/cliente/List/helpers';
import { useVendasContext } from './Context';
import { formatDateTime, getApiErrorMessage, getDiscountRuleLabel } from './helpers';
import { SaleItemsGroup, SaleTotals } from './SaleParts';
import { VendasService } from './Service';
import { TriangleAlert } from 'lucide-react';

const service = new VendasService();

const STATUS_LABELS: Record<string, string> = {
    ATIVA: 'Ativa',
    PAUSADA: 'Pausada',
    CANCELADA: 'Cancelada',
};

export function RecurringSaleDialog({ target, onClose }: { target: any; onClose: () => void }) {
    const { reloadVendas, reloadRecorrentes } = useVendasContext();
    const { successSnackbar, errorSnackbar } = useAppContext();
    const [selling, setSelling] = useState(false);

    const handleConfirm = async () => {
        try {
            setSelling(true);
            await service.createByRecorrente(target._id);
            successSnackbar('Venda registrada. O estoque foi baixado.');
            await Promise.all([reloadVendas(), reloadRecorrentes()]);
            onClose();
        } catch (error: any) {
            console.error(error);
            errorSnackbar(getApiErrorMessage(error, 'Não foi possível registrar a venda.'));
            setSelling(false);
        }
    };

    return (
        <ConfirmDialog
            title={`Vender para ${target.clientName || 'o cliente'}?`}
            confirmLabel={`Vender · ${formatBRL(target.recurringTotal)}`}
            cancelLabel="Voltar"
            destructive={false}
            loading={selling}
            onConfirm={() => void handleConfirm()}
            onClose={onClose}
        >
            <div className="space-y-3">
                <SaleItemsGroup items={target.items} />
                <SaleTotals
                    subTotal={Number(target.recurringSubTotal || 0)}
                    discountAmount={Number(target.recurringDiscountAmount || 0)}
                    discountRule={getDiscountRuleLabel(target.discountType, target.discountValue)}
                    total={Number(target.recurringTotal || 0)}
                />
                <p>Ao confirmar, a venda é registrada e o estoque é baixado.</p>
            </div>
        </ConfirmDialog>
    );
}

export default function RecurringDetail() {
    const { estabSession } = useEstabsContext();
    const { id, setParams } = useParamsContext();
    const { recorrentes, recorrentesLoading, recorrentesError, reloadRecorrentes } = useVendasContext();
    const { successSnackbar, errorSnackbar } = useAppContext();
    const [saleOpen, setSaleOpen] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [canceling, setCanceling] = useState(false);

    const recorrente = recorrentes.find((item) => item._id === id);

    const handleCancel = async () => {
        try {
            setCanceling(true);
            await service.updateRecorrenteStatus(recorrente._id, 'CANCELADA');
            successSnackbar('Recorrência cancelada.');
            await reloadRecorrentes();
            setCancelOpen(false);
        } catch (error: any) {
            console.error(error);
            errorSnackbar(getApiErrorMessage(error, 'Não foi possível cancelar a recorrência.'));
        } finally {
            setCanceling(false);
        }
    };

    if (!recorrente) {
        if (recorrentesLoading) {
            return (
                <div className="mx-auto w-full max-w-2xl space-y-4 px-2" aria-busy="true">
                    <div className="sf-skeleton h-32" />
                    <div className="sf-skeleton h-24" />
                </div>
            );
        }

        return (
            <div className="sf-state">
                <TriangleAlert aria-hidden />
                <h2>{recorrentesError ? 'Não foi possível carregar a recorrência' : 'Recorrência não encontrada'}</h2>
                {recorrentesError ? (
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => void reloadRecorrentes()}>
                        Tentar novamente
                    </button>
                ) : (
                    <button type="button" className="sf-link" onClick={() => setParams({ action: 'recorrentes' })}>
                        Ver recorrências
                    </button>
                )}
            </div>
        );
    }

    const canceled = recorrente.status === 'CANCELADA';

    return (
        <div className="sf-page mx-auto max-w-2xl space-y-5 px-2">
            <div className="sf-group">
                <div className="sf-info-row">
                    <span>Cliente</span>
                    <span>{recorrente.clientName || 'Cliente'}</span>
                </div>
                <div className="sf-info-row">
                    <span>Situação</span>
                    <span>{STATUS_LABELS[recorrente.status] || recorrente.status}</span>
                </div>
                <div className="sf-info-row">
                    <span>Última venda</span>
                    <span>
                        {recorrente.lastSaleAt
                            ? `${formatDateTime(recorrente.lastSaleAt, estabSession?.timezone)} · ${formatBRL(recorrente.lastSaleTotal)}`
                            : 'Nenhuma'}
                    </span>
                </div>
            </div>

            <section>
                <h2 className="sf-group-title">Produtos com preço fixo</h2>
                <SaleItemsGroup items={recorrente.items} />
            </section>

            <SaleTotals
                subTotal={Number(recorrente.recurringSubTotal || 0)}
                discountAmount={Number(recorrente.recurringDiscountAmount || 0)}
                discountRule={getDiscountRuleLabel(recorrente.discountType, recorrente.discountValue)}
                total={Number(recorrente.recurringTotal || 0)}
            />

            {!canceled && (
                <>
                    <button type="button" className="btn btn-primary w-full" onClick={() => setSaleOpen(true)}>
                        Vender agora
                    </button>
                    <div className="sf-group">
                        <button type="button" className="sf-destructive-row" onClick={() => setCancelOpen(true)}>
                            Cancelar recorrência
                        </button>
                    </div>
                </>
            )}

            {saleOpen && <RecurringSaleDialog target={recorrente} onClose={() => setSaleOpen(false)} />}

            {cancelOpen && (
                <ConfirmDialog
                    title="Cancelar esta recorrência?"
                    confirmLabel="Cancelar recorrência"
                    cancelLabel="Voltar"
                    loading={canceling}
                    onConfirm={() => void handleCancel()}
                    onClose={() => setCancelOpen(false)}
                >
                    Ela não poderá mais gerar vendas e deixa de aparecer como oportunidade na agenda. As vendas já feitas
                    continuam registradas. Não dá para reativar por esta tela.
                </ConfirmDialog>
            )}
        </div>
    );
}
